import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { 
  Box, Typography, Paper, Grid, TextField, FormControl, 
  FormLabel, FormControlLabel, Switch, InputLabel, Select,
  MenuItem, Divider, CircularProgress, Alert, Breadcrumbs,
  Chip, IconButton, Tooltip, Button, Link, DialogContentText, useTheme,
  Slider, Autocomplete, alpha, Zoom, Grow,
  Accordion, AccordionSummary, AccordionDetails,
  Stepper, Step, StepLabel, StepButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import Xarrow, { Xwrapper } from 'react-xarrows';
import { useDropzone } from 'react-dropzone';
import { ModelsIcon } from '../global/CustomIcons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LayersIcon from '@mui/icons-material/Layers';
import TuneIcon from '@mui/icons-material/Tune';
import CodeIcon from '@mui/icons-material/Code';
import MemoryIcon from '@mui/icons-material/Memory';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import BuildIcon from '@mui/icons-material/Build';
import TimelineIcon from '@mui/icons-material/Timeline';
import TransformIcon from '@mui/icons-material/Transform';
import CategoryIcon from '@mui/icons-material/Category';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';
import { createBlueprintRequest, clearState } from '../../redux/actions/blueprints/create';
import {
  fetchRequest as recommendedBlueprintRequest,
  clearState as recommendedBlueprintClear,
} from '../../redux/actions/blueprints/recommended';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import AdvancedModelBuilder from '../network-editor/core/AdvancedModelBuilder';
import EstimatorBuilder from './EstimatorBuilder';
import FrameworkPicker from './FrameworkPicker';
import DataPreprocessingPanel from './DataPreprocessingPanel';
import PipelinePreview from '../common/PipelinePreview';
import {
  ALL_METRICS,
  metricCategory,
  metricHint,
  validateCompilationCompatibility,
} from './utils/compilationCompatibility';
import {
  FRAMEWORK_DISPLAY_TO_CANONICAL,
  wizardKind,
  SKLEARN_ESTIMATORS,
  XGBOOST_HYPERPARAMETERS,
  defaultHyperparameters,
} from './utils/frameworkSchemas';

// Wizard steps. Compilation has been folded into Training as a
// collapsed Advanced section so the same 4 steps work for every
// framework (TF/PyTorch/sklearn/XGBoost).
//
// Step order: Basics first, then Framework. The framework choice
// fundamentally branches the rest of the wizard (network editor vs
// estimator picker, with/without compilation Advanced section), but
// users typically want to name and describe their blueprint before
// picking a framework. Defaults work either way - the framework
// picker handles model_blueprint reset when the kind changes.
const WIZARD_STEPS = [
  { key: 'basics',    label: 'Basics' },
  { key: 'framework', label: 'Framework' },
  { key: 'model',     label: 'Model' },
  { key: 'training',  label: 'Training' },
];
const LAST_STEP_INDEX = WIZARD_STEPS.length - 1;

const CreateBlueprintPage = ({ 
  createBlueprint, 
  clearBlueprintCreate, 
  isLoading, 
  error, 
  createdBlueprint,
  recommendedBlueprint,
  fetchRecommendedBlueprint,
  clearRecommendedBlueprint,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State for the blueprint form
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    purpose: '',
    visibility: 'private',
    categories: [],
    tags: [],
    model_blueprint: {
      model_type: 'sequential',
      layers: []
    },
    metadata: {
      framework: 'TensorFlow',
      framework_version: '2.10.0',
      training_environment: 'cloud',
      model_version: '1.0.0',
      privacy: 'obfuscated'
    },
    training_details: {
      epochs: 200,
      batch_size: 64,
      sequence_length: 20,
      callbacks: [
        {
          type: 'EarlyStopping',
          monitor: 'val_loss',
          patience: 15,
          restore_best_weights: true
        },
        {
          type: 'ReduceLROnPlateau',
          monitor: 'val_loss',
          factor: 0.5,
          patience: 8,
          min_lr: 1e-7
        }
      ]
    },
    compilation_details: {
      loss: 'huber',
      optimizer: {
        type: 'adamw',
        learning_rate: 0.0005,
        weight_decay: 0.01
      },
      metrics: ['mae', 'mse']
    },
    data_preprocessing: {
      signal_scalers: {
        'numeric': 'MinMaxScaler',
        'categorical': 'None',
        'timestamp': 'StandardScaler',
        'text': 'None',
        'binary': 'None'
      },
      global_scaler: 'MinMaxScaler' // This will be used for both features and labels
    },
    data_split: {
      test_split: 0.2,
      validation_split: 0.2
    }
  });

  // Network builder state
  const [layers, setLayers] = useState(formValues.model_blueprint.layers);
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Drag and drop handling
  const canvasRef = useRef(null);
  
  // Add validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Add ref for the AdvancedModelBuilder
  const modelBuilderRef = useRef(null);

  // Validate a specific step. Step indices match WIZARD_STEPS:
  //   0 Basics    — name / description / purpose / categories / tags
  //   1 Framework — visual framework picker
  //   2 Model     — layer editor (TF/PyTorch) OR estimator picker (sklearn/xgboost)
  //   3 Training  — epochs/batch/sequence + data split + Advanced collapsibles
  //                 (Loss/Optimizer/Metrics for NN, callbacks, preprocessing).
  //                 Compilation was folded in here so all frameworks share
  //                 the same 4-step wizard.
  const validateTab = (tabIndex) => {
    let errors = {};
    let isValid = true;

    switch (tabIndex) {
      case 0: // Basics
        if (!formValues.name.trim()) {
          errors.name = 'Model name is required';
          isValid = false;
        }
        if (!formValues.purpose.trim()) {
          errors.purpose = 'Purpose is required';
          isValid = false;
        }
        if (!formValues.description.trim()) {
          errors.description = 'Description is required';
          isValid = false;
        }
        if (formValues.categories.length === 0) {
          errors.categories = 'At least one category is required';
          isValid = false;
        }
        if (formValues.tags.length === 0) {
          errors.tags = 'At least one tag is required';
          isValid = false;
        }
        break;

      case 1: // Framework
        // The picker always has a default value so this is always valid.
        // Kept as an explicit case for future per-framework gating
        // (e.g. license acceptance, GPU availability checks).
        break;

      case 2: // Architecture (network editor OR estimator picker)
        if (wizardKind(formValues.metadata.framework) === 'estimator') {
          // sklearn / xgboost: validate the picked estimator. The BE
          // validator runs the actual fit on synthetic data, so
          // wizard-side checks are minimal.
          const fwCanonical = FRAMEWORK_DISPLAY_TO_CANONICAL[formValues.metadata.framework];
          if (fwCanonical === 'sklearn' && !formValues.model_blueprint.estimator) {
            errors.layers = 'Pick an sklearn estimator';
            isValid = false;
          }
          break;
        }
        if (modelBuilderRef.current && modelBuilderRef.current.syncLayers) {
          modelBuilderRef.current.syncLayers();
        }
        if (!layers || layers.length < 1) {
          errors.layers = 'At least 1 layer is required';
          isValid = false;
        }
        if (modelBuilderRef.current && modelBuilderRef.current.validateModel) {
          const validation = modelBuilderRef.current.validateModel();
          if (!validation.isValid) {
            errors.layers = 'The neural network model contains compatibility issues that must be fixed';
            isValid = false;
          }
        }
        break;

      case 3: // Training (Compilation folded in here as Advanced)
        if (!formValues.training_details.epochs || formValues.training_details.epochs < 1) {
          errors.epochs = 'Epochs must be at least 1';
          isValid = false;
        }
        if (!formValues.training_details.batch_size || formValues.training_details.batch_size < 1) {
          errors.batch_size = 'Batch size must be at least 1';
          isValid = false;
        }
        if (!formValues.training_details.sequence_length || formValues.training_details.sequence_length < 1) {
          errors.sequence_length = 'Sequence length must be at least 1';
          isValid = false;
        }
        if (
          formValues.data_split.test_split + formValues.data_split.validation_split > 0.5
        ) {
          errors.data_split = 'test_split + validation_split must not exceed 0.5';
          isValid = false;
        }
        // Compilation requirements (NN frameworks only).
        if (wizardKind(formValues.metadata.framework) === 'neural_network') {
          if (
            !formValues.compilation_details.metrics ||
            formValues.compilation_details.metrics.length === 0
          ) {
            errors.metrics = 'At least one metric must be selected';
            isValid = false;
          }
          if (!formValues.compilation_details.loss) {
            errors.loss = 'A loss function must be selected';
            isValid = false;
          }
          const compatWarnings = validateCompilationCompatibility(
            formValues.compilation_details
          );
          if (compatWarnings.length > 0) {
            errors.compatibility = compatWarnings[0];
            isValid = false;
          }
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Validate ALL steps at submit-time so the user can't bypass an
  // earlier step's rules.
  const validateAllTabs = () => {
    let allValid = true;
    for (let i = 0; i <= LAST_STEP_INDEX; i++) {
      // validateTab calls setValidationErrors with the result for that
      // tab only; we re-merge after all tabs have run so the user sees
      // the full list of problems.
      const ok = validateTab(i);
      if (!ok) {
        // setValidationErrors is async so we can't read it back here -
        // mark allValid false and let the per-tab call accumulate
        // visible errors after the loop.
        allValid = false;
      }
    }
    return allValid;
  };

  // Step navigation. WIZARD_STEPS:
  //   0 Framework | 1 Basics | 2 Model | 3 Training (incl. Compilation Advanced)
  const handleNext = () => {
    // Sync network-editor layers into formValues before advancing
    // off the Model step (it maintains its own internal state).
    if (currentTab === 2 && modelBuilderRef.current && modelBuilderRef.current.syncLayers) {
      modelBuilderRef.current.syncLayers();
    }
    if (currentTab === 2) {
      setFormValues(prev => ({
        ...prev,
        model_blueprint: {
          ...prev.model_blueprint,
          layers: layers
        }
      }));
    }

    if (validateTab(currentTab)) {
      if (
        currentTab === 2 &&
        modelBuilderRef.current &&
        wizardKind(formValues.metadata.framework) === 'neural_network'
      ) {
        const isModelValid = modelBuilderRef.current.isValid();
        if (!isModelValid) {
          setValidationErrors(prev => ({
            ...prev,
            layers: 'The neural network model contains compatibility issues. Please fix them before proceeding.'
          }));
          return;
        }
      }
      setCurrentTab(Math.min(currentTab + 1, LAST_STEP_INDEX));
    }
  };

  const handleBack = () => {
    if (currentTab === 0) {
      navigate('/models');
    } else {
      setCurrentTab(prevTab => prevTab - 1);
    }
  };
  
  // Handle blueprint creation. Triggered ONLY by an explicit click on
  // the "Save blueprint" button on the last step - never via form
  // submission, because the wizard has many nested components
  // (NetworkEditor, NodeControls, BaseNode) with their own buttons
  // that would otherwise accidentally submit. The wizard wrapper is a
  // <Box>, not a <form>, so the only path into this handler is the
  // explicit Save button click.
  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    // Sync the model builder layers into form state so validation sees
    // the latest architecture (the builder maintains its own state).
    if (modelBuilderRef.current && modelBuilderRef.current.syncLayers) {
      modelBuilderRef.current.syncLayers();
    }

    const updatedFormValues = {
      ...formValues,
      model_blueprint: {
        ...formValues.model_blueprint,
        layers: layers,
      },
    };

    // Validate every tab. If anything fails, jump the user to the
    // first failing tab so the inline error markers are immediately
    // visible.
    if (!validateAllTabs()) {
      // Find the earliest tab that has an error. Re-run validateTab
      // for each in order so we get the visible setValidationErrors
      // for the failing tab last (so the user sees its errors).
      for (let i = 0; i <= LAST_STEP_INDEX; i++) {
        if (!validateTab(i)) {
          setCurrentTab(i);
          break;
        }
      }
      return;
    }

    // Architecture-level model validity check (uses the builder's
    // isValid() which is stricter than the layer-count check). Only
    // applies to neural-network frameworks - estimator frameworks
    // don't have a layer architecture to validate.
    if (
      modelBuilderRef.current &&
      wizardKind(formValues.metadata.framework) === 'neural_network'
    ) {
      const isModelValid = modelBuilderRef.current.isValid();
      if (!isModelValid) {
        setValidationErrors((prev) => ({
          ...prev,
          layers:
            'The neural network model contains compatibility issues. Please fix them before proceeding.',
        }));
        setCurrentTab(2); // jump to Architecture tab
        return;
      }
    }
    
    // Get the workspace ID from localStorage
    const workspace_id = localStorage.getItem('WORKSPACE_ID');
    
    if (!workspace_id) {
      console.error('No workspace ID found in localStorage');
      return;
    }
    
    
    // Use the model builder ref to get the JSON directly if available
    let finalFormValues = { ...updatedFormValues };
    
    // Get the full model configuration from the ModelBuilder component
    if (modelBuilderRef.current && modelBuilderRef.current.generateModelBlueprintJSON) {
      try {
        const modelConfig = modelBuilderRef.current.generateModelBlueprintJSON();
        
        // Update form values with the model config
        finalFormValues = {
          ...finalFormValues,
          model_blueprint: modelConfig.model_blueprint
        };
      } catch (error) {
        console.error('Error getting model JSON from ref:', error);
      }
    } else {
    }
    
    // Add workspace_id + canonical framework alias to the form data.
    // The backend's validate_custom_blueprint reads BOTH the top-level
    // `framework` field AND `metadata.framework`; sending the
    // canonical lowercase alias at the top level makes intent
    // unambiguous and lets us drop the legacy alias detection step.
    // For estimator-kind frameworks we also strip the now-irrelevant
    // compilation_details so the BE doesn't try to validate them.
    const canonicalFw = FRAMEWORK_DISPLAY_TO_CANONICAL[finalFormValues.metadata.framework] || 'tensorflow';
    const isEstimator = wizardKind(finalFormValues.metadata.framework) === 'estimator';
    const blueprintData = {
      ...finalFormValues,
      framework: canonicalFw,
      workspace_id,
      ...(isEstimator
        ? { compilation_details: {}, training_details: {
            ...finalFormValues.training_details,
            // sklearn / xgboost don't run epochs, so drop the
            // neural-network-specific knobs that don't apply.
            callbacks: [],
          }}
        : {}),
    };

    // Create the blueprint
    createBlueprint(blueprintData);
    
  };
  
  // Debug: Log layers whenever they change
  useEffect(() => {
  }, [formValues.model_blueprint.layers]);
  
  // Debugging - log the form values when they change
  useEffect(() => {
  }, [formValues]);

  // Add back the missing effect to handle successful creation of blueprint
  useEffect(() => {
    if (createdBlueprint) {
      // Navigate to the blueprint detail page
      navigate(`/model/${createdBlueprint.uid}`);
      // Clear the state
      clearBlueprintCreate();
    }
  }, [createdBlueprint, navigate, clearBlueprintCreate]);

  // Load the analysis-suggested blueprint so a fresh blueprint starts from
  // the recommendation (replaces the old "Suggest with AI" step). The
  // workspace analysis worker auto-creates one "recommended" blueprint; we
  // fetch the list and pre-fill the wizard from it, once.
  const prefilledFromRecRef = useRef(false);
  const recWorkspaceId =
    typeof window !== 'undefined' ? localStorage.getItem('WORKSPACE_ID') : null;
  const [recBlueprint, setRecBlueprint] = useState(null);

  // Apply the recommended blueprint into the wizard. ``force`` overwrites
  // user-entered values (used by the "Use recommendation" button); otherwise
  // we only fill blanks (used by the one-time auto-prefill on load).
  const applyRecommendation = (bp, { force = false } = {}) => {
    if (!bp) return;
    setFormValues((prev) => ({
      ...prev,
      name: (force ? bp.name : prev.name || bp.name) || '',
      description: (force ? bp.description : prev.description || bp.description) || '',
      purpose: (force ? bp.purpose : prev.purpose || bp.purpose) || '',
      categories: force ? (bp.categories || []) : (prev.categories && prev.categories.length ? prev.categories : (bp.categories || [])),
      tags: force ? (bp.tags || []) : (prev.tags && prev.tags.length ? prev.tags : (bp.tags || [])),
      model_blueprint: bp.model_blueprint || prev.model_blueprint,
      metadata: { ...prev.metadata, ...(bp.metadata || {}) },
      training_details: bp.training_details || prev.training_details,
      compilation_details: bp.compilation_details || prev.compilation_details,
      data_split: bp.data_split || prev.data_split,
      data_preprocessing: bp.data_preprocessing || prev.data_preprocessing,
    }));
    if (bp.model_blueprint && Array.isArray(bp.model_blueprint.layers)) {
      setLayers(bp.model_blueprint.layers);
    }
  };

  // Fetch the workspace's analysis-recommended blueprint (workspace-scoped, so it
  // works for any workspace member) and auto-prefill the wizard from it once.
  // The saga swallows errors: no recommendation yet is a normal state and
  // the wizard silently stays manual.
  useEffect(() => {
    fetchRecommendedBlueprint({ workspaceId: recWorkspaceId });
    return () => { clearRecommendedBlueprint(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recWorkspaceId]);

  useEffect(() => {
    if (!recommendedBlueprint) return;
    setRecBlueprint(recommendedBlueprint);
    if (!prefilledFromRecRef.current) {
      prefilledFromRecRef.current = true;
      applyRecommendation(recommendedBlueprint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedBlueprint]);

  // Trigger fit view when switching to the Architecture tab (index 2
  // in the new wizard layout). Network editor lays out its xarrows
  // after first render, so we re-fit a beat after the tab becomes
  // visible.
  useEffect(() => {
    if (currentTab === 2 && modelBuilderRef.current && modelBuilderRef.current.fitView) {
      const timer = setTimeout(() => {
        modelBuilderRef.current.fitView();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentTab]);
  
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {/* Breadcrumb Navigation */}
      <PageBreadcrumbs
        items={[
          {
            text: "Models",
            icon: <ModelsIcon />,
            link: "/models"
          },
          {
            text: "Model studio"
          }
        ]}
        sx={{ mb: 2 }}
      />
      
      {/* === Wizard stepper === Four steps total. Compilation is folded
          into Training as a collapsed Advanced section, so the same
          step count works for every framework. Steps are clickable so
          users can jump back to fix something earlier in the flow. */}
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          mt: 2,
          px: { xs: 2, md: 4 },
          py: 2.5,
          borderRadius: 3,
          borderColor: theme.palette.divider,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Stepper
          activeStep={currentTab}
          nonLinear
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiStepLabel-label': {
              fontSize: '13px',
              fontWeight: 500,
            },
            '& .MuiStepLabel-label.Mui-active': {
              fontWeight: 700,
              color: theme.palette.text.primary,
            },
            '& .MuiStepLabel-label.Mui-completed': {
              color: theme.palette.text.secondary,
            },
          }}
        >
          {WIZARD_STEPS.map((step, idx) => (
            <Step key={step.key} completed={idx < currentTab}>
              <StepButton
                onClick={() => setCurrentTab(idx)}
                disableRipple
                sx={{ py: 0 }}
              >
                <StepLabel>{step.label}</StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>
        {/* AI blueprint generation removed: the recommended model now comes
            from the single workspace-analysis LLM pass (auto-selected when
            creating a model), so there's no separate "Suggest with AI" step. */}
      </Paper>

      {/* Analysis recommendation banner. Surfaces the workspace's recommended
          model and lets the user re-apply it; warns when the current
          framework diverges from the recommendation. */}
      {recBlueprint && (() => {
        const recModel = recBlueprint?.metadata?.recommended_model || recBlueprint?.model_blueprint?.estimator || 'recommended';
        const recFwDisplay = recBlueprint?.metadata?.framework || '';
        const currentFw = formValues.metadata.framework || '';
        const mismatch = recFwDisplay && currentFw && recFwDisplay !== currentFw;
        return (
          <Alert
            severity={mismatch ? 'warning' : 'info'}
            icon={<AutoFixHighIcon fontSize="inherit" />}
            sx={{ mt: 2 }}
            action={
              <CMButtonOutlined size="small" onClick={() => applyRecommendation(recBlueprint, { force: true })}>
                Use recommendation
              </CMButtonOutlined>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Analysis recommends <b>{recModel}</b>{recFwDisplay ? ` (${recFwDisplay})` : ''}.
            </Typography>
            {mismatch && (
              <Typography variant="caption" color="text.secondary">
                You're currently configuring {currentFw}. Click "Use recommendation" to load the suggested {recFwDisplay} model.
              </Typography>
            )}
          </Alert>
        );
      })()}

      {/* Wizard wrapper is a <Box>, not a <form>. The wizard contains
          many nested editors (network editor, node controls, etc.)
          with their own buttons that would default to type="submit"
          inside a form and accidentally create the blueprint
          mid-wizard. By NOT being a form, the only way to trigger
          handleSubmit() is via the explicit Save blueprint button's
          onClick on the last step. */}
      <Box
        onKeyDown={(e) => {
          // Enter inside a single-line text input would normally
          // submit a containing form. We're not a form anymore so
          // this is just defense-in-depth in case a child does
          // something unexpected.
          const isMultiline = e.target?.tagName === 'TEXTAREA';
          if (e.key === 'Enter' && !isMultiline && currentTab < LAST_STEP_INDEX) {
            e.preventDefault();
          }
        }}
      >
        {/* === Step 1 — Framework picker === */}
        <Box sx={{ display: currentTab === 1 ? 'block' : 'none' }}>
          <Box sx={{ py: 1 }}>
            <FrameworkPicker
              value={formValues.metadata.framework}
              onChange={(newFramework) => {
                const newKind = wizardKind(newFramework);
                setFormValues((prev) => {
                  let newModelBp = prev.model_blueprint;
                  if (newKind === 'estimator') {
                    if (newFramework === 'XGBoost') {
                      newModelBp = {
                        hyperparameters: defaultHyperparameters(XGBOOST_HYPERPARAMETERS),
                      };
                    } else {
                      const def = SKLEARN_ESTIMATORS[0];
                      newModelBp = {
                        estimator: def.name,
                        hyperparameters: defaultHyperparameters(def.hyperparameters),
                      };
                    }
                  } else {
                    newModelBp = {
                      model_type: 'sequential',
                      layers: prev.model_blueprint.layers || [],
                    };
                  }
                  return {
                    ...prev,
                    metadata: { ...prev.metadata, framework: newFramework },
                    model_blueprint: newModelBp,
                  };
                });
                if (newKind === 'estimator') {
                  setLayers([]);
                }
              }}
            />
          </Box>
        </Box>

        {/* === Step 0 — Basics === Name, description, purpose, tags. */}
        <Box sx={{ display: currentTab === 0 ? 'block' : 'none' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Model basics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              A few details so you (and others) can find this model later.
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3, borderColor: theme.palette.divider }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Model name"
                  placeholder="e.g. Airline passenger LSTM"
                  value={formValues.name}
                  onChange={(e) => setFormValues(prev => ({ ...prev, name: e.target.value }))}
                  required
                  error={!!validationErrors.name}
                  helperText={validationErrors.name || ' '}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="visibility-label">Visibility</InputLabel>
                  <Select
                    labelId="visibility-label"
                    value={formValues.visibility}
                    onChange={(e) => setFormValues(prev => ({ ...prev, visibility: e.target.value }))}
                    label="Visibility"
                  >
                    <MenuItem value="private">Private — only you</MenuItem>
                    <MenuItem value="public">Public — visible in marketplace</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Purpose"
                  placeholder="One sentence: what is this model for?"
                  value={formValues.purpose}
                  onChange={(e) => setFormValues(prev => ({ ...prev, purpose: e.target.value }))}
                  error={!!validationErrors.purpose}
                  helperText={validationErrors.purpose || ' '}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="A longer explanation of what the model does, the data it expects, and any caveats."
                  value={formValues.description}
                  onChange={(e) => setFormValues(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={3}
                  error={!!validationErrors.description}
                  helperText={validationErrors.description || ' '}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={['prediction', 'stocks', 'classification', 'regression', 'time-series']}
                  value={formValues.categories}
                  onChange={(e, newValue) => setFormValues(prev => ({ ...prev, categories: newValue }))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categories"
                      placeholder="Add category"
                      helperText={validationErrors.categories || 'Press Enter to add'}
                      error={!!validationErrors.categories}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={['lstm', 'gru', 'cnn', 'transformer', 'stock market', 'weather']}
                  value={formValues.tags}
                  onChange={(e, newValue) => setFormValues(prev => ({ ...prev, tags: newValue }))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tag"
                      helperText={validationErrors.tags || 'Press Enter to add'}
                      error={!!validationErrors.tags}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
          
        </Box>

        {/* === Tab 2 — Architecture (was tab 1) ===
            For TF / PyTorch we render the network-editor (AdvancedModelBuilder)
            with a layer palette. For sklearn / XGBoost we render the
            EstimatorBuilder with an estimator picker + hyperparameter form.
            wizardKind() picks the right variant from the user's framework choice. */}
        <Box sx={{ display: currentTab === 2 ? 'block' : 'none' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {wizardKind(formValues.metadata.framework) === 'estimator'
                ? 'Pick your estimator'
                : 'Design your network'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {wizardKind(formValues.metadata.framework) === 'estimator'
                ? 'Choose an estimator and tune its hyperparameters. The platform handles the rest.'
                : 'Add layers, configure them, and the platform validates the architecture before saving.'}
            </Typography>
          </Box>

          {wizardKind(formValues.metadata.framework) === 'estimator' ? (
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 3, borderColor: theme.palette.divider }}>
              <EstimatorBuilder
                framework={
                  FRAMEWORK_DISPLAY_TO_CANONICAL[formValues.metadata.framework] || 'sklearn'
                }
                modelBlueprint={formValues.model_blueprint}
                onChange={(newModelBp) => {
                  setFormValues((prev) => ({ ...prev, model_blueprint: newModelBp }));
                }}
              />
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: theme.palette.divider, mb: 3 }}>
              <Box sx={{ height: 700 }}>
                <AdvancedModelBuilder
                  ref={modelBuilderRef}
                  layers={layers}
                  setLayers={(newLayers) => {
                    setLayers(newLayers);
                    setFormValues(prev => ({
                      ...prev,
                      model_blueprint: {
                        ...prev.model_blueprint,
                        layers: newLayers
                      }
                    }));
                  }}
                  validationError={validationErrors.layers}
                />
              </Box>
            </Paper>
          )}
        </Box>
        
        {/* === Step 3 — Training === */}
        <Box sx={{ display: currentTab === 3 ? 'block' : 'none' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Training settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              How long to train, how to split your data, and what to optimize for. Defaults work for most models — open Advanced to fine-tune.
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3, borderColor: theme.palette.divider }}>
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 2.5, fontSize: '16px' }}>Training parameters</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth
                  label="Epochs"
                  name="training_details.epochs"
                  value={formValues.training_details.epochs}
                  onChange={(e) => {
                    setFormValues(prev => ({
                      ...prev,
                      training_details: {
                        ...prev.training_details,
                        epochs: parseInt(e.target.value)
                      }
                    }));
                  }}
                  type="number"
                  InputProps={{ inputProps: { min: 1, max: 10000 } }}
                  helperText={validationErrors.epochs || "Number of times to iterate over the entire dataset (1-10000)"}
                  error={!!validationErrors.epochs}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth
                  label="Batch Size"
                  name="training_details.batch_size"
                  value={formValues.training_details.batch_size}
                  onChange={(e) => {
                    setFormValues(prev => ({
                      ...prev,
                      training_details: {
                        ...prev.training_details,
                        batch_size: parseInt(e.target.value)
                      }
                    }));
                  }}
                  type="number"
                  InputProps={{ inputProps: { min: 1, max: 2048 } }}
                  helperText={validationErrors.batch_size || "Number of samples per gradient update (powers of 2 recommended)"}
                  error={!!validationErrors.batch_size}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth
                  label="Sequence Length"
                  name="training_details.sequence_length"
                  value={formValues.training_details.sequence_length}
                  onChange={(e) => {
                    setFormValues(prev => ({
                      ...prev,
                      training_details: {
                        ...prev.training_details,
                        sequence_length: parseInt(e.target.value)
                      }
                    }));
                  }}
                  type="number"
                  InputProps={{ inputProps: { min: 1, max: 1000 } }}
                  helperText="For sequence-based models like LSTM/GRU (1-1000)"
                />
              </Grid>
            </Grid>

            {/* Min-segment-size hint. Real preview lives on the model
                creation page where the segment + horizon are known;
                here we show two reference points so users understand
                how their sequence_length affects the minimum segment
                size they'll need at training time. */}
            <PipelinePreview
              segment={null}
              sequenceLength={formValues.training_details.sequence_length}
              forecastHorizon={formValues.training_details.sequence_length}
              dataSplit={formValues.data_split}
              compact
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3, borderColor: theme.palette.divider }}>
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 2.5, fontSize: '16px' }}>Data split</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Test Split: {formValues.data_split.test_split * 100}%</Typography>
                <Slider
                  value={formValues.data_split.test_split * 100}
                  onChange={(e, newValue) => {
                    // Ensure validation + test <= 0.5 (50%)
                    const testSplit = newValue / 100;
                    const validationSplit = formValues.data_split.validation_split;
                    
                    if (testSplit + validationSplit <= 0.5) {
                      setFormValues(prev => ({
                        ...prev,
                        data_split: {
                          ...prev.data_split,
                          test_split: testSplit
                        }
                      }));
                    }
                  }}
                  min={5}
                  max={30}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography gutterBottom>Validation Split: {formValues.data_split.validation_split * 100}%</Typography>
                <Slider
                  value={formValues.data_split.validation_split * 100}
                  onChange={(e, newValue) => {
                    // Ensure validation + test <= 0.5 (50%)
                    const validationSplit = newValue / 100;
                    const testSplit = formValues.data_split.test_split;
                    
                    if (testSplit + validationSplit <= 0.5) {
                      setFormValues(prev => ({
                        ...prev,
                        data_split: {
                          ...prev.data_split,
                          validation_split: validationSplit
                        }
                      }));
                    }
                  }}
                  min={5}
                  max={30}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${value}%`}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  height: 50, 
                  display: 'flex',
                  width: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mt: 2
                }}>
                  <Box 
                    sx={{ 
                      flex: 1 - formValues.data_split.test_split - formValues.data_split.validation_split,
                      bgcolor: theme.palette.success.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    Training {Math.round((1 - formValues.data_split.test_split - formValues.data_split.validation_split) * 100)}%
                  </Box>
                  <Box 
                    sx={{ 
                      flex: formValues.data_split.validation_split,
                      bgcolor: theme.palette.info.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    Validation {Math.round(formValues.data_split.validation_split * 100)}%
                  </Box>
                  <Box 
                    sx={{ 
                      flex: formValues.data_split.test_split,
                      bgcolor: theme.palette.warning.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    Test {Math.round(formValues.data_split.test_split * 100)}%
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Callbacks (EarlyStopping / ReduceLROnPlateau) tucked
              behind a collapsed Advanced accordion. Most users never
              touch these - the defaults are sensible and EarlyStopping
              is opt-in. Hidden entirely for sklearn / xgboost since
              they don't support iterative-training callbacks. */}
          {wizardKind(formValues.metadata.framework) === 'neural_network' && (
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Advanced — Early stopping & learning-rate scheduling
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.training_details.callbacks.some(c => c.type === 'EarlyStopping')}
                    onChange={(e) => {
                      let updatedCallbacks = [...formValues.training_details.callbacks];
                      
                      if (e.target.checked) {
                        // Add EarlyStopping if it doesn't exist
                        if (!updatedCallbacks.some(c => c.type === 'EarlyStopping')) {
                          updatedCallbacks.push({
                            type: 'EarlyStopping',
                            monitor: 'val_loss',
                            patience: 10
                          });
                        }
                      } else {
                        // Remove EarlyStopping if it exists
                        updatedCallbacks = updatedCallbacks.filter(c => c.type !== 'EarlyStopping');
                      }
                      
                      setFormValues(prev => ({
                        ...prev,
                        training_details: {
                          ...prev.training_details,
                          callbacks: updatedCallbacks
                        }
                      }));
                    }}
                  />
                }
                label="Use Early Stopping"
              />
            </Box>
            
            {formValues.training_details.callbacks.some(c => c.type === 'EarlyStopping') && (
              <Box sx={{ pl: 4, mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="early-stopping-monitor-label">Monitor</InputLabel>
                      <Select
                        labelId="early-stopping-monitor-label"
                        value={formValues.training_details.callbacks.find(c => c.type === 'EarlyStopping')?.monitor || 'val_loss'}
                        onChange={(e) => {
                          const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                            if (callback.type === 'EarlyStopping') {
                              return { ...callback, monitor: e.target.value };
                            }
                            return callback;
                          });
                          
                          setFormValues(prev => ({
                            ...prev,
                            training_details: {
                              ...prev.training_details,
                              callbacks: updatedCallbacks
                            }
                          }));
                        }}
                        label="Monitor"
                      >
                        <MenuItem value="val_loss">Validation Loss</MenuItem>
                        <MenuItem value="val_accuracy">Validation Accuracy</MenuItem>
                        <MenuItem value="val_mae">Validation MAE</MenuItem>
                        <MenuItem value="val_mse">Validation MSE</MenuItem>
                        <MenuItem value="loss">Training Loss</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField 
                      fullWidth
                      label="Patience"
                      name="training_details.callbacks.early_stopping.patience"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 100 } }}
                      value={formValues.training_details.callbacks.find(c => c.type === 'EarlyStopping')?.patience || 15}
                      onChange={(e) => {
                        const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                          if (callback.type === 'EarlyStopping') {
                            return { ...callback, patience: parseInt(e.target.value) };
                          }
                          return callback;
                        });
                        
                        setFormValues(prev => ({
                          ...prev,
                          training_details: {
                            ...prev.training_details,
                            callbacks: updatedCallbacks
                          }
                        }));
                      }}
                      helperText="Number of epochs with no improvement after which training will stop"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formValues.training_details.callbacks.find(c => c.type === 'EarlyStopping')?.restore_best_weights || false}
                          onChange={(e) => {
                            const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                              if (callback.type === 'EarlyStopping') {
                                return { ...callback, restore_best_weights: e.target.checked };
                              }
                              return callback;
                            });
                            
                            setFormValues(prev => ({
                              ...prev,
                              training_details: {
                                ...prev.training_details,
                                callbacks: updatedCallbacks
                              }
                            }));
                          }}
                        />
                      }
                      label="Restore Best Weights"
                      sx={{ mt: 2 }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* ModelCheckpoint toggle was removed (Phase 8 wizard cleanup):
                checkpoints written to /tmp inside the training pod were
                discarded on pod termination, and the platform always saves
                the trained model to S3 via save_model_to_s3 anyway. The
                trainer still HONORS old blueprints with a ModelCheckpoint
                callback for backwards compat, but new blueprints don't
                need to expose the toggle. */}

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.training_details.callbacks.some(c => c.type === 'ReduceLROnPlateau')}
                    onChange={(e) => {
                      let updatedCallbacks = [...formValues.training_details.callbacks];
                      
                      if (e.target.checked) {
                        // Add ReduceLROnPlateau if it doesn't exist
                        if (!updatedCallbacks.some(c => c.type === 'ReduceLROnPlateau')) {
                          updatedCallbacks.push({
                            type: 'ReduceLROnPlateau',
                            monitor: 'val_loss',
                            factor: 0.5,
                            patience: 8,
                            min_lr: 1e-7
                          });
                        }
                      } else {
                        // Remove ReduceLROnPlateau if it exists
                        updatedCallbacks = updatedCallbacks.filter(c => c.type !== 'ReduceLROnPlateau');
                      }
                      
                      setFormValues(prev => ({
                        ...prev,
                        training_details: {
                          ...prev.training_details,
                          callbacks: updatedCallbacks
                        }
                      }));
                    }}
                  />
                }
                label="Reduce Learning Rate on Plateau"
              />
            </Box>
            
            {formValues.training_details.callbacks.some(c => c.type === 'ReduceLROnPlateau') && (
              <Box sx={{ pl: 4, mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="reduce-lr-monitor-label">Monitor</InputLabel>
                      <Select
                        labelId="reduce-lr-monitor-label"
                        value={formValues.training_details.callbacks.find(c => c.type === 'ReduceLROnPlateau')?.monitor || 'val_loss'}
                        onChange={(e) => {
                          const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                            if (callback.type === 'ReduceLROnPlateau') {
                              return { ...callback, monitor: e.target.value };
                            }
                            return callback;
                          });
                          
                          setFormValues(prev => ({
                            ...prev,
                            training_details: {
                              ...prev.training_details,
                              callbacks: updatedCallbacks
                            }
                          }));
                        }}
                        label="Monitor"
                      >
                        <MenuItem value="val_loss">Validation Loss</MenuItem>
                        <MenuItem value="val_accuracy">Validation Accuracy</MenuItem>
                        <MenuItem value="val_mae">Validation MAE</MenuItem>
                        <MenuItem value="val_mse">Validation MSE</MenuItem>
                        <MenuItem value="loss">Training Loss</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField 
                      fullWidth
                      label="Factor"
                      name="training_details.callbacks.reduce_lr_on_plateau.factor"
                      type="number"
                      InputProps={{ inputProps: { min: 0.1, max: 0.9, step: 0.1 } }}
                      value={formValues.training_details.callbacks.find(c => c.type === 'ReduceLROnPlateau')?.factor || 0.5}
                      onChange={(e) => {
                        const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                          if (callback.type === 'ReduceLROnPlateau') {
                            return { ...callback, factor: parseFloat(e.target.value) };
                          }
                          return callback;
                        });
                        
                        setFormValues(prev => ({
                          ...prev,
                          training_details: {
                            ...prev.training_details,
                            callbacks: updatedCallbacks
                          }
                        }));
                      }}
                      helperText="Factor by which learning rate will be reduced"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField 
                      fullWidth
                      label="Patience"
                      name="training_details.callbacks.reduce_lr_on_plateau.patience"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 50 } }}
                      value={formValues.training_details.callbacks.find(c => c.type === 'ReduceLROnPlateau')?.patience || 8}
                      onChange={(e) => {
                        const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                          if (callback.type === 'ReduceLROnPlateau') {
                            return { ...callback, patience: parseInt(e.target.value) };
                          }
                          return callback;
                        });
                        
                        setFormValues(prev => ({
                          ...prev,
                          training_details: {
                            ...prev.training_details,
                            callbacks: updatedCallbacks
                          }
                        }));
                      }}
                      helperText="Epochs to wait before reducing LR"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField 
                      fullWidth
                      label="Min Learning Rate"
                      name="training_details.callbacks.reduce_lr_on_plateau.min_lr"
                      type="number"
                      InputProps={{ inputProps: { min: 1e-10, max: 1e-3, step: 1e-8 } }}
                      value={formValues.training_details.callbacks.find(c => c.type === 'ReduceLROnPlateau')?.min_lr || 1e-7}
                      onChange={(e) => {
                        const updatedCallbacks = formValues.training_details.callbacks.map(callback => {
                          if (callback.type === 'ReduceLROnPlateau') {
                            return { ...callback, min_lr: parseFloat(e.target.value) };
                          }
                          return callback;
                        });
                        
                        setFormValues(prev => ({
                          ...prev,
                          training_details: {
                            ...prev.training_details,
                            callbacks: updatedCallbacks
                          }
                        }));
                      }}
                      helperText="Lower bound on learning rate"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            </AccordionDetails>
          </Accordion>
          )}


          {/* Loss / Optimizer / Metrics for neural-network frameworks
              (TF / PyTorch). Folded in here as a collapsed Advanced
              section so the wizard stays at 4 steps total - sklearn
              and XGBoost have these baked into the estimator and
              don't see this section at all. */}
          {wizardKind(formValues.metadata.framework) === 'neural_network' && (
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Advanced — Loss, optimizer &amp; metrics
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
              Loss function & optimizer
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="loss-function-label">Loss Function</InputLabel>
                  <Select
                    labelId="loss-function-label"
                    name="compilation_details.loss"
                    value={formValues.compilation_details.loss}
                    onChange={(e) => {
                      setFormValues(prev => ({
                        ...prev,
                        compilation_details: {
                          ...prev.compilation_details,
                          loss: e.target.value
                        }
                      }));
                    }}
                    label="Loss Function"
                  >
                    <MenuItem value="mse">Mean Squared Error (MSE)</MenuItem>
                    <MenuItem value="mae">Mean Absolute Error (MAE)</MenuItem>
                    <MenuItem value="huber">Huber Loss</MenuItem>
                    <MenuItem value="logcosh">Log-Cosh Loss</MenuItem>
                    <MenuItem value="binary_crossentropy">Binary Crossentropy</MenuItem>
                    <MenuItem value="categorical_crossentropy">Categorical Crossentropy</MenuItem>
                    <MenuItem value="sparse_categorical_crossentropy">Sparse Categorical Crossentropy</MenuItem>
                    <MenuItem value="focal_loss">Focal Loss</MenuItem>
                    <MenuItem value="hinge">Hinge Loss</MenuItem>
                    <MenuItem value="squared_hinge">Squared Hinge Loss</MenuItem>
                    <MenuItem value="cosine_similarity">Cosine Similarity</MenuItem>
                    <MenuItem value="kl_divergence">KL Divergence</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="optimizer-label">Optimizer</InputLabel>
                  <Select
                    labelId="optimizer-label"
                    name="compilation_details.optimizer.type"
                    value={formValues.compilation_details.optimizer.type}
                    onChange={(e) => {
                      setFormValues(prev => ({
                        ...prev,
                        compilation_details: {
                          ...prev.compilation_details,
                          optimizer: {
                            ...prev.compilation_details.optimizer,
                            type: e.target.value
                          }
                        }
                      }));
                    }}
                    label="Optimizer"
                  >
                    <MenuItem value="adam">Adam</MenuItem>
                    <MenuItem value="adamw">AdamW</MenuItem>
                    <MenuItem value="adamax">Adamax</MenuItem>
                    <MenuItem value="nadam">Nadam</MenuItem>
                    <MenuItem value="sgd">Stochastic Gradient Descent (SGD)</MenuItem>
                    <MenuItem value="rmsprop">RMSprop</MenuItem>
                    <MenuItem value="adagrad">Adagrad</MenuItem>
                    <MenuItem value="adadelta">Adadelta</MenuItem>
                    <MenuItem value="ftrl">FTRL</MenuItem>
                    <MenuItem value="lion">Lion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth
                  label="Learning Rate"
                  name="compilation_details.optimizer.learning_rate"
                  value={formValues.compilation_details.optimizer.learning_rate}
                  onChange={(e) => {
                    setFormValues(prev => ({
                      ...prev,
                      compilation_details: {
                        ...prev.compilation_details,
                        optimizer: {
                          ...prev.compilation_details.optimizer,
                          learning_rate: parseFloat(e.target.value)
                        }
                      }
                    }));
                  }}
                  type="number"
                  inputProps={{ step: 0.0001, min: 0.0001, max: 1 }}
                  helperText="Recommended range: 0.0001 to 0.01"
                />
              </Grid>
              {['adamw', 'sgd'].includes(formValues.compilation_details.optimizer.type) && (
                <Grid item xs={12} md={6}>
                  <TextField 
                    fullWidth
                    label="Weight Decay"
                    name="compilation_details.optimizer.weight_decay"
                    value={formValues.compilation_details.optimizer.weight_decay || 0}
                    onChange={(e) => {
                      setFormValues(prev => ({
                        ...prev,
                        compilation_details: {
                          ...prev.compilation_details,
                          optimizer: {
                            ...prev.compilation_details.optimizer,
                            weight_decay: parseFloat(e.target.value)
                          }
                        }
                      }));
                    }}
                    type="number"
                    inputProps={{ step: 0.001, min: 0, max: 1 }}
                    helperText="L2 regularization parameter (0 = no regularization)"
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* LearningRateScheduler card was removed (overlapped with
              ReduceLROnPlateau). The trainer still honors old
              blueprints with a LearningRateScheduler callback for
              backwards compat. */}

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
              Performance metrics
            </Typography>

            {(() => {
              const compatWarnings = validateCompilationCompatibility(
                formValues.compilation_details
              );
              if (compatWarnings.length === 0) return null;
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {compatWarnings.map((w, i) => (
                    <Typography key={i} variant="body2">{w}</Typography>
                  ))}
                </Alert>
              );
            })()}

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Performance Metrics
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Regression metrics are computed directly. Classification-style
                metrics are wrapped to compute per-step against the platform's
                3D forecasting output.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {ALL_METRICS.map((metric) => {
                  const isSelected = formValues.compilation_details.metrics.includes(metric);
                  const cat = metricCategory(metric);
                  const tooltip = metricHint(metric);
                  const chip = (
                    <Chip
                      key={metric}
                      label={metric.toUpperCase().replace(/_/g, ' ')}
                      clickable
                      color={isSelected ? 'primary' : 'default'}
                      variant={cat === 'wrapped' ? 'outlined' : 'filled'}
                      onClick={() => {
                        let updatedMetrics = [...formValues.compilation_details.metrics];
                        if (updatedMetrics.includes(metric)) {
                          updatedMetrics = updatedMetrics.filter((m) => m !== metric);
                        } else {
                          updatedMetrics.push(metric);
                        }
                        setFormValues((prev) => ({
                          ...prev,
                          compilation_details: {
                            ...prev.compilation_details,
                            metrics: updatedMetrics,
                          },
                        }));
                      }}
                      sx={{
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.1)
                          : undefined,
                        // Slightly muted style on wrapped chips so users
                        // can see at a glance which group is which.
                        opacity: cat === 'wrapped' && !isSelected ? 0.85 : 1,
                      }}
                    />
                  );
                  return tooltip ? (
                    <Tooltip key={metric} title={tooltip} arrow placement="top">
                      <span>{chip}</span>
                    </Tooltip>
                  ) : (
                    chip
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Selected metrics: {formValues.compilation_details.metrics.length > 0
                  ? formValues.compilation_details.metrics.join(', ')
                  : 'None'}
              </Typography>
              {validationErrors.metrics && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {validationErrors.metrics}
                </Typography>
              )}
            </Box>
          </Box>
            </AccordionDetails>
          </Accordion>
          )}

          {/* Data preprocessing folded into the Training tab as a
              collapsed Advanced accordion - the standalone Data
              Preprocessing tab was removed in the wizard simplification.
              Defaults (MinMaxScaler for numeric data) work for the
              vast majority of forecasting blueprints; only power
              users need to override them. */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Advanced — Data preprocessing & scaling
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <DataPreprocessingPanel
                value={formValues.data_preprocessing}
                onChange={(newDp) => setFormValues((prev) => ({
                  ...prev,
                  data_preprocessing: newDp,
                }))}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Wizard nav bar — sticky-feeling but inline. Cancel left, Back/Next/Save right. */}
        {error && (
          <Alert severity="error" sx={{ mt: 3, mb: 0 }}>{error}</Alert>
        )}
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            mb: 4,
            px: { xs: 2, md: 3 },
            py: 2,
            borderRadius: 3,
            borderColor: theme.palette.divider,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* IMPORTANT: every button in this form must have an explicit
              ``type``. Without it, native <button> defaults to "submit"
              inside a form, so clicking Next (or pressing Enter while a
              text field is focused) would trigger handleSubmit() and
              create the blueprint mid-wizard. Only the final Save
              button uses type="submit". */}
          <CMButtonOutlined
            type="button"
            onClick={() => navigate('/models')}
            disabled={isLoading}
          >
            Cancel
          </CMButtonOutlined>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {currentTab > 0 && (
              <CMButtonOutlined
                type="button"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </CMButtonOutlined>
            )}
            {currentTab >= LAST_STEP_INDEX ? (
              <CMButton
                type="button"
                variant="contained"
                onClick={handleSubmit}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={isLoading}
              >
                {isLoading ? 'Saving…' : 'Save model'}
              </CMButton>
            ) : (
              <CMButton
                type="button"
                variant="contained"
                endIcon={<NavigateNextIcon />}
                onClick={handleNext}
                disabled={isLoading}
              >
                Next
              </CMButton>
            )}
          </Box>
        </Paper>
      </Box>

    </Box>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state.blueprint_create?.isLoading || false,
  error: state.blueprint_create?.error || null,
  createdBlueprint: state.blueprint_create?.data || null,
  recommendedBlueprint: state.blueprint_recommended?.response || null,
});

const mapDispatchToProps = (dispatch) => ({
  createBlueprint: (data) => dispatch(createBlueprintRequest(data)),
  clearBlueprintCreate: () => dispatch(clearState()),
  fetchRecommendedBlueprint: (payload) => dispatch(recommendedBlueprintRequest(payload)),
  clearRecommendedBlueprint: () => dispatch(recommendedBlueprintClear()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateBlueprintPage); 